import { CloseRounded } from "@mui/icons-material";
import { Button } from "@mui/material";
import { SnackbarKey, useSnackbar } from "notistack";
import { FC, useEffect, ReactNode, useRef } from "react";
import styles from "@/components/notification/genericNotification.module.css";

interface Props {
  message: string | any | null;
  variant: "success" | "error" | "warning" | "info" | undefined;
  handleCloseSnackbar: () => void;
  persist?: boolean;
  snackbarKeyRef?: React.MutableRefObject<SnackbarKey | null>;
  showCloseButton?: boolean;
  additionalContent?: (key: SnackbarKey) => ReactNode;
}

const GenericNotification: FC<Props> = ({
  message,
  variant,
  handleCloseSnackbar,
  persist = false,
  snackbarKeyRef,
  showCloseButton = true,
  additionalContent,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const consumedMessageRef = useRef<string | null>(null);

  let snackMessage: string | null = null;

  if (typeof message === "string") {
    snackMessage = message;
  } else if (Array.isArray(message)) {
    snackMessage = message[0] ?? null;
  } else if (typeof message === "object" && message !== null) {
    const mapped = Object.entries(message).map(
      ([fieldName, msg]: any) => `${fieldName}: ${msg}`,
    );
    snackMessage = mapped[0] ?? null;
  }
  if (Array.isArray(snackMessage)) {
    snackMessage = snackMessage[0];
  }

  useEffect(() => {
    if (!snackMessage) return;

    if (consumedMessageRef.current === snackMessage) {
      return;
    }
    consumedMessageRef.current = snackMessage;

    if (snackbarKeyRef?.current) {
      closeSnackbar(snackbarKeyRef.current);
    }

    const key = enqueueSnackbar(snackMessage, {
      variant,
      persist,
      action: (key: SnackbarKey) => {
        const closeButton = showCloseButton ? (
          <Button
            onClick={() => {
              closeSnackbar(key);

              handleCloseSnackbar?.();
            }}
            size="small"
            sx={{ color: "#fff" }}
          >
            <CloseRounded />
          </Button>
        ) : null;

        if (additionalContent) {
          return (
            <div className={styles.additionalContentWrapper}>
              {additionalContent(key)}
              {closeButton}
            </div>
          );
        }

        return closeButton;
      },
    });

    if (snackbarKeyRef) {
      snackbarKeyRef.current = key;
    }

    handleCloseSnackbar();
    consumedMessageRef.current = null;
  }, [
    message,
    variant,
    persist,
    enqueueSnackbar,
    closeSnackbar,
    snackbarKeyRef,
    handleCloseSnackbar,
    showCloseButton,
    additionalContent,
  ]);

  return null;
};

export default GenericNotification;
